
import {
  defineComponent,
  computed,
  watch,
  onMounted,
  ref,
  onBeforeMount,
} from "vue";
import "@fullcalendar/core/vdom";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/vue3";
import { Draggable } from "@fullcalendar/interaction";
import AddScheduleModal from "@/components/modals/forms/AddScheduleModal.vue";
import { ScheduleApi } from "@/services/api/Schedule.service";
import { TranslationService } from "@/core/services/TranslationService";
import { FormApi } from "@/services/api/Form.service";
import { configDay } from "./schedularConfig";
import VZFilter from "@/components/kt-datatable/components/VZFilter.vue";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import { IForm } from "@/interfaces/iForm";
import { useToast } from "vue-toastification";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { IDataTableFilterOption } from "@/interfaces/iPageModel";
import { setDateRangeShortcuts } from "@/components/typography/DateRangeOptions";
import { FormatMethods, getTimeZoneList } from "@/helpers/data-format";
import { GET_SELECTED_SITES_STR, SITE_STORE } from "@/store/userSite/types";
import { UserAPI } from "@/services/api/User.service";
import { SiteApi } from "@/services/api/Site.service";
import { hasPermission } from "@/core/services/permission.service";
import store from "@/store";
import { Actions } from "@/store/enums/StoreEnums";
import { v4 as uuidv4 } from "uuid";
import { useRoute } from "vue-router";
import { usePopover, useSite } from "./schedularComposable";
export default defineComponent({
  components: {
    FullCalendar,
    "vz-add-schedule": AddScheduleModal,
    "vz-filter": VZFilter,
    FormDetailModal,
  },
  setup() {
    const t = (key) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const { formatArgs, hidePopovers, initPopovers, getClass } = usePopover();
    const { sites, isValidEvent } = useSite();
    const forms = ref<any>({});
    const scheduleFormRef = ref<HTMLFormElement>();
    const searchTerm = ref();
    const searchTermEvent = ref();
    const shortcuts = ref(setDateRangeShortcuts());
    const filters = ref();
    const editData = ref<any>({});
    const show = ref<any>(false);
    const calendarOptions = ref<any>(configDay());
    const showDetailDialog = ref<any>(false);
    const selectedForm = ref<Partial<IForm>>({});
    const scheduleSuccessMsg = t("pages.scheduler.scheduleSuccessMsg");
    const editScheduledMsg = t("pages.scheduler.editScheduledMsg");
    const eventDenyMsg = t("pages.scheduler.cannotCreateTaskForDate");
    const taskDeleteMsg = t("pages.scheduler.taskDeletedMsg");
    const taskDeleteErrorMsg = t("pages.scheduler.taskDeleteErrorMsg");
    const invalidFormSceduleMsg = t("validation.invalidFormSchedule");
    const calendarRef = ref();
    const loading = ref(false);
    const users = ref<any>({});
    const route = useRoute();
    const userTitle = ref("");
    const scheduleFilter = ref<Array<IDataTableFilterOption>>([
      {
        key: "siteIds",
        label: TranslationService.translate("common.sites"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "site/filter/SITES",
        textKey: "text",
        valueKey: "value",
      },
      {
        key: "formDefinitionIds",
        label: TranslationService.translate("pages.form.title"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "forms/filter/FORM_NAME",
        textKey: "text",
        valueKey: "value",
      },
      {
        key: "userIds",
        label: TranslationService.translate("common.users"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "security/filter/USERS",
        textKey: "text",
        valueKey: "value",
      },
    ]);
    const siteId = computed(() => {
      return route.params.siteId as string;
    });
    const userId = computed(() => {
      return route.params.userId as string;
    });
    const calendarDate = computed(() => {
      return route.query.date || moment().format("YYYY-MM-DD");
    });
    const isEditable = ref<boolean>(false);
    const timeZoneList = computed(() => getTimeZoneList());

    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.scheduler.scheduler"), [
        "Home",
        t("pages.scheduler.scheduler"),
      ]);
      // calendarOptions.value.initialDate = calendarDate.value;
      await Promise.all([
        SiteApi.getInstance().getScheduleSite({
          size: 100000,
        }),
        UserAPI.getInstance().getUserList({
          size: 100000,
        }),
        loadForm({ size: 10000, status: "ACTIVE" }),
        loadSchedullingData(),
      ]).then((values) => {
        sites.value = values[0].content;
        sites.value.sort((a, b) => (a.name > b.name ? 1 : -1));
        users.value = values[1].content;
        users.value.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
        const selectedUser = users.value.find(
          (user) => user.webId == userId.value
        );
        userTitle.value = `${selectedUser?.firstName} ${selectedUser?.lastName}`;
      });
      // calendarRef.value.getApi().gotoDate(calendarDate.value);
      calendarOptions.value.dateClick = (e) => {
        if (hasPermission("VYZOR:APPLICATIONS:SCHEDULER:CREATE")) {
          hidePopovers();
          editData.value = {};
          editData.value.start = e.dateStr;
          editData.value.end = moment(e.dateStr).add(30, "minute").format();
          editData.value.resourceId = userId.value + "_" + siteId.value;
          editData.value.siteId = Number(siteId.value);
          editData.value.assignUserId = userId.value;
          editData.value.selectedSite = sites.value.find(
            (site) => site.webId == siteId.value
          );
          editData.value.title = "";
          isEditable.value = true;
          scheduleFormRef?.value?.resetForm();
          show.value = true;
        }
      };
      calendarOptions.value.eventAllow = (dropLocation, draggedEvent) => {
        const formId = draggedEvent.id;
        return isValidEvent(formId, siteId.value, userId.value);
      };

      calendarOptions.value.customButtons = {
        prev: {
          text: "Prev",
          click: function () {
            calendarRef.value.getApi().prev();
            refresh();
          },
        },
        next: {
          text: "Next",
          click: function () {
            calendarRef.value.getApi().next();
            refresh();
          },
        },
        today: {
          text: "Today",
          click: function () {
            calendarRef.value.getApi().today();
            refresh();
          },
        },
      };

      calendarOptions.value.eventClick = (e) => {
        if (hasPermission("VYZOR:APPLICATIONS:SCHEDULER:UPDATE")) {
          hidePopovers();
          console.log(e);
          editData.value = {};
          // editData.value = calendarOptions.value.events.find(
          //   (obj) => obj.identifier == e.event.extendedProps.identifier
          // );

          const eventData = e.event.extendedProps;
          editData.value = {
            id: e.event.id,
            start: e.event.startStr,
            end: e.event.endStr,
            siteId: eventData.siteId,
            assignUserId: eventData.assignUserId,
            formDefinitionId: eventData.formDefinitionId,
            formName: eventData.formName,
            documentId: eventData.documentId,
            resourceId: userId.value + "_" + siteId.value,
          };

          isEditable.value =
            eventData.userId == eventData.assignUserId &&
            eventData.scheduleType == "SCHEDULED" &&
            hasPermission("VYZOR:APPLICATIONS:SCHEDULER:UPDATE");
          scheduleFormRef?.value?.resetForm();
          show.value = true;
        }
      };
      calendarOptions.value.eventMouseEnter = (arg) => {
        const { siteName, formName, assignUserId } = arg.event.extendedProps;
        const user = users.value.find((user) => user.webId == assignUserId);
        formatArgs({
          id: arg.event.id,
          title: formName ?? "",
          user: user?.firstName + " " + user?.lastName,
          site: siteName,
          form: formName,
          startStr: arg.event.startStr,
          endStr: arg.event.endStr,
          allDay: arg.event.allDay,
          notesCount: arg.event.extendedProps.notesCount,
          conversationCount: arg.event.extendedProps.chatCount,
        });
        // Show popover preview
        initPopovers(arg.el);
      };
      calendarOptions.value.eventMouseLeave = (arg) => {
        hidePopovers();
      };

      calendarOptions.value.eventDragStart = (obj) => {
        editData.value.id = obj?.event?._def?.publicId;
      };
      calendarOptions.value.eventDrop = (obj) => {
        let eventData = {} as any;
        eventData.startDate = moment(
          obj?.event?._instance?.range?.start
        ).format();
        eventData.endDate = moment(obj?.event?._instance?.range?.end)
          .add(30, "minute")
          .format();
        eventData.userAccountId = userId.value;
        eventData.siteId = siteId.value;
        eventData.formDefinitionId = obj?.event?._def?.extendedProps.formDefId;
        eventData.webId = editData.value.id;
        updateData(eventData);
      };
      calendarOptions.value.drop = (e) => {
        const user = users.value.find(
          (user) => user.webId == e.resource._resource.extendedProps.userId
        );
        if (moment() > moment(e.dateStr)) {
          toast.error(eventDenyMsg);
          e.remove();
          return false;
        } else {
          editData.value = {};
          editData.value.start = e.dateStr;
          editData.value.end = moment(e.dateStr).add(30, "minute").format();
          editData.value.resourceId = userId.value + "_" + siteId.value;
          editData.value.id = e.draggedEl.id;
          editData.value.siteId = siteId.value;
          editData.value.selectedSite = sites.value.find(
            (site) => site.webId == siteId.value
          );
          editData.value.pnUuid = user?.pnUuid;
          editData.value.documentName = e.draggedEl.innerText;
          createEvent(editData.value);
          // return false;
        }
      };
      // await loadForm({ size: 10000, status: "ACTIVE" });
      new Draggable(
        document.getElementById("draggable-events") as HTMLElement,
        {
          itemSelector: ".drop-event",
          eventData: function (eventEl) {
            return {
              title: eventEl.innerText,
              id: eventEl.id,
            };
          },
        }
      );
    });

    const createEvent = (data) => {
      const userAccountId = data.resourceId.split("_")[1];
      let eventData = {} as any;
      eventData.status = "SCHEDULE"; // Required by backend
      eventData.documentName = data.documentName ?? "";
      eventData.type = null; // Required by backend(Hamza changed type=100 to null)
      eventData.fileId = null;
      eventData.flow = null;
      eventData.latitude = null;
      eventData.longitude = null;
      eventData.timeFrame = null;
      eventData.formScheduleId = null;
      eventData.completedDate = null;
      eventData.selectedSite = data.selectedSite;
      eventData.startDate = moment(data.start).format();
      eventData.endDate = moment(data.end).subtract(1, "millisecond").format();
      eventData.userAccountId = userAccountId;
      eventData.siteId = data.siteId;
      eventData.formDefinitionId = data.id;
      eventData.pnUuid = data.pnUuid;
      submitData(eventData);
    };
    const loadSchedullingData = async () => {
      try {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        calendarRef.value.getApi().removeAllEvents();
        calendarOptions.value.events = [];
        await getAllScheduleDocuments();
      } catch (error) {
        //ERROR
      } finally {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };

    // getAllScheduleDocuments
    const getAllScheduleDocuments = async () => {
      // get end date from currentEnd
      const endDate = calendarRef.value.getApi().view.currentEnd;
      // minus 1 millisecond from end date
      const endDateMinusOneMillisecond = moment(endDate)
        .subtract(1, "millisecond")
        .format("YYYY-MM-DDTHH:mm:ssZ");
      await ScheduleApi.getInstance()
        .getAllScheduleDocuments({
          startDate: moment(calendarRef.value.getApi().getDate())
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
          endDate: endDateMinusOneMillisecond,
          ...filters.value,
          siteIds: siteId.value,
          userIds: userId.value,
          search: searchTermEvent.value,
        })
        .then((data) => {
          let events = prepareEvents(data);
          calendarOptions.value.events = [];
          calendarOptions.value.events =
            calendarOptions.value.events.concat(events);
        })
        .catch((error) => {
          // ERROR
        })
        .finally(() => {
          //TODO
        });
    };
    const refresh = async () => {
      try {
        await Promise.all([loadSchedullingData()]);
      } catch (error) {
        //
      }
    };

    const loadForm = async (params) => {
      await FormApi.getInstance()
        .getFormList(params)
        .then((res) => {
          forms.value = res.content;
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const searchData = async () => {
      await loadForm({
        size: 10000,
        status: "ACTIVE",
        search: searchTerm.value,
      });
    };

    const searchFilter = async () => {
      if (
        searchTermEvent.value.length == 0 ||
        searchTermEvent.value.length >= 3
      ) {
        await refresh();
      }
    };

    const submitData = async (data) => {
      if (moment() > moment(data.startDate)) {
        toast.error(eventDenyMsg);
        return false;
      }

      if (
        !isValidEvent(data.formDefinitionId, data.siteId, data.userAccountId)
      ) {
        toast.error(invalidFormSceduleMsg);
        return;
      }

      const selectedTimeZone: any = timeZoneList.value.find(
        (timezone) => timezone.value == data?.selectedSite?.timezone
      );

      data.startDate = `${data.startDate}${selectedTimeZone.offset}`;
      data.endDate = `${data.endDate}${selectedTimeZone.offset}`;

      loading.value = true;

      await ScheduleApi.getInstance()
        .createSchedule(data)
        .then(async (res) => {
          toast.success(scheduleSuccessMsg);
          loadSchedullingData();
          loading.value = false;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          // TODO
        });
    };

    const updateData = async (data) => {
      if (moment() > moment(data.startDate)) {
        toast.error(eventDenyMsg);
        return false;
      }
      if (
        !isValidEvent(data.formDefinitionId, data.siteId, data.userAccountId)
      ) {
        toast.error(invalidFormSceduleMsg);
        return;
      }
      data.selectedSite = sites.value.find((site) => site.webId == data.siteId);
      const selectedTimeZone: any = timeZoneList.value.find(
        (timezone) => timezone.value == data?.selectedSite?.timezone
      );

      data.startDate = `${data.startDate}${selectedTimeZone.offset}`;
      data.endDate = `${data.endDate}${selectedTimeZone.offset}`;

      loading.value = true;
      await ScheduleApi.getInstance()
        .updateSchedule(data)
        .then(async (res) => {
          toast.success(editScheduledMsg);
          loadSchedullingData();
          loading.value = false;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          // TODO
        });
    };

    const openDetailModal = async (formWebId) => {
      showDetailDialog.value = true;
      selectedForm.value = {};
      const form = await FormApi.getInstance().getFormById(formWebId);
      selectedForm.value = form;
    };

    const showModal = () => {
      editData.value = {};
      scheduleFormRef?.value?.resetForm();
      isEditable.value = true;
      show.value = true;
    };

    const prepareEvents = (data) => {
      return data.map((obj) => {
        return {
          id: obj.documentId,
          formDefId: obj.formDefinitionId,
          resourceId: obj.siteId + "_" + obj.assignUserId,
          start: FormatMethods.convertToLocalTimeZone(obj.startDate),
          end: FormatMethods.convertToLocalTimeZone(obj.endDate),
          title: obj.formName, // Assuming you decided to remove the commented title line
          classNames: getClass(obj.scheduleType),
          identifier: uuidv4(),
          ...obj,
        };
      });
    };

    const applyFilter = (updatedFilters) => {
      filters.value = updatedFilters;
      refresh();
    };

    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES_STR}`;
      return store.getters[key] as string;
    });

    watch(selectedSites, async () => {
      await Promise.all([
        loadSchedullingData(),
        loadForm({
          size: 10000,
          status: "ACTIVE",
          search: searchTerm.value,
        }),
      ]);
    });

    const deleteTask = async (data) => {
      try {
        loading.value = true;
        await FormApi.getInstance().deleteDocument(data.webId);
        toast.success(taskDeleteMsg);
        await loadSchedullingData();
        show.value = false;
        const closeButton = document.getElementById("closeButtonH");
        if (closeButton) {
          closeButton.click();
        }
      } catch (error) {
        console.error("Error deleting task:", error);
        toast.error(taskDeleteErrorMsg);
      } finally {
        loading.value = false;
      }
    };
    return {
      timeZoneList,
      isEditable,
      calendarOptions,
      forms,
      shortcuts,
      searchTerm,
      searchTermEvent,
      searchData,
      submitData,
      editData,
      updateData,
      showModal,
      scheduleFilter,
      applyFilter,
      calendarRef,
      loading,
      refresh,
      selectedForm,
      openDetailModal,
      show,
      hasPermission,
      showDetailDialog,
      t,
      siteId,
      userId,
      calendarDate,
      userTitle,
      searchFilter,
      deleteTask,
    };
  },
});
